import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { PATH_META } from './GardenPage';
import { bookBelongsToStoryPath, getBookStoryPath } from '../utils/storyPath';
import { FAVOURITE_BOOKS_PATH_ID } from '../utils/favouriteBooks';
import { filterBooksByLanguage } from '../utils/bookFilters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NestPage = ({ onOpenReader, onNavigate }) => {
  const { user, token } = useAuth();
  const { theme, setTheme } = useTheme();
  const [currentBook, setCurrentBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surpriseError, setSurpriseError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [currentRes, booksRes] = await Promise.all([
        fetch(`${API}/progress/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/books`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (currentRes.ok) {
        const data = await currentRes.json();
        setCurrentBook(data);
      }

      if (booksRes.ok) {
        const booksData = await booksRes.json();
        setBooks(booksData);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const featuredStoryPath = useMemo(() => {
    const currentId = currentBook?.book?.id;
    if (currentId) {
      const full = books.find((b) => b.id === currentId);
      if (full) return getBookStoryPath(full);
    }
    const inProgress = books.find(
      (b) => (b.progress?.current_page || 0) > 0 && !b.progress?.completed
    );
    if (inProgress) return getBookStoryPath(inProgress);

    const pathIds = [...new Set(books.map((b) => getBookStoryPath(b)))];
    if (pathIds.length === 0) return null;

    let best = pathIds[0];
    let bestCount = 0;
    pathIds.forEach((pathId) => {
      const count = books.filter((b) => bookBelongsToStoryPath(b, pathId)).length;
      if (count > bestCount) {
        best = pathId;
        bestCount = count;
      }
    });
    return best;
  }, [currentBook, books]);

  const featuredPathBooks = useMemo(() => {
    if (!featuredStoryPath) return [];
    return books
      .filter((b) => bookBelongsToStoryPath(b, featuredStoryPath))
      .sort((a, b) => {
        const aReading =
          (a.progress?.current_page || 0) > 0 && !a.progress?.completed;
        const bReading =
          (b.progress?.current_page || 0) > 0 && !b.progress?.completed;
        if (aReading !== bReading) return aReading ? -1 : 1;
        const aStarted = (a.progress?.current_page || 0) > 0;
        const bStarted = (b.progress?.current_page || 0) > 0;
        if (aStarted !== bStarted) return aStarted ? -1 : 1;
        return a.title.localeCompare(b.title);
      });
  }, [books, featuredStoryPath]);

  const favouriteBooks = useMemo(() => {
    return books
      .filter((b) => b.is_favourite)
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [books]);

  const favouritePathLabel = PATH_META[FAVOURITE_BOOKS_PATH_ID]?.label || 'Favourite Books';

  const featuredPathLabel = useMemo(() => {
    if (!featuredStoryPath) return '';
    return (
      PATH_META[featuredStoryPath]?.label ||
      featuredStoryPath.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }, [featuredStoryPath]);

  const getJourneyNodeStatus = (b, currentId) => {
    if (b.progress?.completed) return 'completed';
    if (
      b.id === currentId ||
      ((b.progress?.current_page || 0) > 0 && !b.progress?.completed)
    ) {
      return 'active';
    }
    return 'upcoming';
  };

  const shortenTitle = (title, max = 18) => {
    const t = String(title || '').trim();
    if (t.length <= max) return t;
    const words = t.split(/\s+/);
    if (words.length > 1 && `${words[0]} ${words[1]}`.length <= max) {
      return `${words[0]} ${words[1]}`;
    }
    return `${t.slice(0, max - 1)}…`;
  };

  const handleSurpriseMe = () => {
    const englishBooks = filterBooksByLanguage(books, 'english');
    if (englishBooks.length === 0) {
      setSurpriseError('No English books in the library yet. Visit the Garden to browse.');
      return;
    }
    setSurpriseError('');
    const unread = englishBooks.filter((b) => !b.progress?.completed);
    const pool = unread.length > 0 ? unread : englishBooks;
    const randomBook = pool[Math.floor(Math.random() * pool.length)];
    onOpenReader(randomBook.id);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅 Good morning';
    if (hour < 17) return '☀️ Good afternoon';
    return '🌙 Good evening';
  };

  const themes = [
    { id: 'forest', name: 'Forest' },
    { id: 'ocean', name: 'Ocean' },
    { id: 'sunset', name: 'Sunset' },
    { id: 'berry', name: 'Berry' },
    { id: 'night', name: 'Night' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <div style={{ fontSize: '48px' }} className="animate-float">📚</div>
      </div>
    );
  }

  const book = currentBook?.book;
  const progress = currentBook?.progress;
  const progressPct = book ? Math.round((progress?.current_page || 0) / book.pages * 100) : 0;

  return (
    <div className="animate-slide-up" data-testid="nest-page">
      {/* Stats Bar */}
      <div className="nest-stats-bar">
        <div className="nest-greeting">
          {getGreeting()}, {user?.name?.split(' ')[0] || 'Reader'}!
        </div>
        <div className="theme-bar-swatches">
          {themes.map(t => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              data-testid={`theme-${t.id}`}
              data-theme={t.id}
              className={`swatch ${theme === t.id ? 'active' : ''}`}
              title={t.name}
            />
          ))}
        </div>
      </div>

      {/* Hero Card */}
      <div className="nest-hero">
        <div className="nest-hero-left">
          <div className="hero-badge">
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="none" stroke="currentColor" strokeWidth="2"></path>
              <circle cx="12" cy="10" r="3" fill="none" stroke="currentColor" strokeWidth="2"></circle>
            </svg>
            Pick up where you left off
          </div>

          <h1 className="hero-headline" style={{ fontSize: '2.5rem', lineHeight: '1.1', marginBottom: '0.75rem' }}>
            Continue your<br />adventure
          </h1>

          {book ? (
            <>
              <p className="hero-body" style={{ marginBottom: '0.75rem' }}>
                {book.emoji} needs you! You stopped just before page {(progress?.current_page || 0) + 1}. 
                What happens next? 🌧️
              </p>

              <div className="hero-chips" style={{ marginBottom: '0.75rem' }}>
                <span className="hero-chip">
                  {book.title} · page {progress?.current_page || 1} of {book.pages}
                </span>
                <span className="hero-chip">
                  ⏱ About {Math.ceil((book.pages - (progress?.current_page || 0)) * 0.5)} min to read
                </span>
              </div>

              {/* Book Metadata Section */}
              <div style={{ 
                marginTop: '0.5rem',
                marginBottom: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                {/* Language & Genre Row */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {book.language && (
                    <span style={{
                      padding: '0.375rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      🌐 {book.language.charAt(0).toUpperCase() + book.language.slice(1)}
                    </span>
                  )}
                  {book.genre && (
                    <span style={{
                      padding: '0.375rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      {book.genre === 'fiction' ? '📖' : '📚'} {book.genre.charAt(0).toUpperCase() + book.genre.slice(1)}
                    </span>
                  )}
                  {book.annotation && (
                    <span style={{
                      padding: '0.375rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      ✨ {book.annotation}
                    </span>
                  )}
                </div>

                {/* Keywords Row */}
                {book.keywords && book.keywords.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {book.keywords.slice(0, 5).map((keyword, idx) => (
                      <span key={idx} style={{
                        padding: '0.25rem 0.625rem',
                        background: 'rgba(255, 255, 255, 0.12)',
                        color: '#ffffff',
                        borderRadius: '0.375rem',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                      }}>
                        #{keyword}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Bookmark/Progress Indicator */}
              {progress?.last_location && (
                <div className="hero-chips">
                  <span className="hero-chip">
                    🌿 You stopped at {progress.last_location}
                  </span>
                </div>
              )}

              <button
                onClick={() => onOpenReader(book.id)}
                data-testid="continue-reading-btn"
                className="hero-cta"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill="none" stroke="currentColor" strokeWidth="2"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 2.5 0 0 1 6.5 2z" fill="none" stroke="currentColor" strokeWidth="2"></path>
                </svg>
                Continue Reading {book.emoji}
                <span style={{ marginLeft: 'auto', opacity: 0.5 }}>→</span>
              </button>
            </>
          ) : (
            <>
              <p className="hero-body">
                Start your reading journey today! Pick a book from your Story Path.
              </p>
              <button
                onClick={() => onNavigate('garden')}
                data-testid="start-reading-btn"
                className="hero-cta"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill="none" stroke="currentColor" strokeWidth="2"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="none" stroke="currentColor" strokeWidth="2"></path>
                </svg>
                Start Your First Book!
                <span style={{ marginLeft: 'auto', opacity: 0.5 }}>→</span>
              </button>
            </>
          )}
        </div>

        {/* Book Preview Card */}
        {book && (
          <div 
            className="hero-book-card"
            onClick={() => onOpenReader(book.id)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onOpenReader(book.id);
              }
            }}
          >
            <div className="hbc-label-row">
              <span className="hbc-label">Tonight's story</span>
              <span className="hbc-level">Level {book.level}</span>
            </div>
            <div className="hbc-title">{book.title}</div>
            <div className="hbc-illus">
              {book.cover_image ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${book.cover_image}`} 
                  alt={book.title}
                  onError={(e) => {
                    console.error('Failed to load cover image:', book.cover_image);
                    e.target.style.display = 'none';
                    e.target.parentElement.textContent = book.emoji || '📚';
                  }}
                />
              ) : (
                book.emoji || '📚'
              )}
            </div>
            <div className="hbc-progress-row">
              <div className="hbc-bar">
                <div className="hbc-bar-fill" style={{ width: `${progressPct}%` }}></div>
              </div>
              <span className="hbc-progress-pct">{progressPct}%</span>
            </div>
            <div className="hbc-hint">Keep reading to find out what happens!</div>
          </div>
        )}
      </div>

      {/* Surprise Me Card */}
      <button
        type="button"
        onClick={handleSurpriseMe}
        data-testid="surprise-me-btn"
        className="nest-surprise"
      >
        <div className="ns-dice">🚀</div>
        <div className="ns-text">
          <div className="ns-headline">Surprise me!</div>
          <div className="ns-sub">Let BookBlossom pick an English book just for you ✨</div>
        </div>
        <div className="ns-arrow">🍃</div>
      </button>

      {surpriseError ? (
        <p className="nest-surprise-lang-error" data-testid="surprise-error">
          {surpriseError}
        </p>
      ) : null}

      {/* Story Path journey — horizontal cover circles */}
      {featuredPathBooks.length > 0 && (
        <section className="nest-story-journey" data-testid="nest-story-journey">
          <div className="nest-story-journey-header">
            <div className="nest-story-journey-label">
              <span className="nest-story-journey-pin" aria-hidden>📍</span>
              <span>YOUR STORY PATH</span>
            </div>
            <span className="nest-story-journey-path-name">
              Story Path · {featuredPathLabel}
            </span>
          </div>

          <div className="nest-story-journey-track">
            {featuredPathBooks.map((pathBook, idx) => {
              const nodeStatus = getJourneyNodeStatus(pathBook, book?.id);
              const connectorActive =
                idx > 0 &&
                (nodeStatus === 'active' ||
                  nodeStatus === 'completed' ||
                  getJourneyNodeStatus(featuredPathBooks[idx - 1], book?.id) !== 'upcoming');

              return (
                <React.Fragment key={pathBook.id}>
                  {idx > 0 && (
                    <div
                      className={`nest-story-journey-connector${connectorActive ? ' is-active' : ''}`}
                      aria-hidden
                    />
                  )}
                  <button
                    type="button"
                    className="nest-story-journey-node"
                    data-testid={`nest-journey-book-${pathBook.id}`}
                    onClick={() => onOpenReader(pathBook.id)}
                  >
                    <div
                      className={`nest-story-journey-circle nest-story-journey-circle--${nodeStatus}`}
                    >
                      {pathBook.cover_image ? (
                        <img
                          src={`${BACKEND_URL}${pathBook.cover_image}`}
                          alt=""
                          className="nest-story-journey-cover"
                          onError={(ev) => {
                            ev.target.style.display = 'none';
                            const wrap = ev.target.parentElement;
                            if (wrap && !wrap.querySelector('.nest-story-journey-fallback')) {
                              const span = document.createElement('span');
                              span.className = 'nest-story-journey-fallback';
                              span.textContent = pathBook.emoji || '📚';
                              wrap.appendChild(span);
                            }
                          }}
                        />
                      ) : (
                        <span className="nest-story-journey-fallback">
                          {pathBook.emoji || '📚'}
                        </span>
                      )}
                    </div>
                    <span className="nest-story-journey-title">
                      {shortenTitle(pathBook.title)}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </section>
      )}

      {favouriteBooks.length > 0 && (
        <section className="nest-story-journey" data-testid="nest-favourite-journey">
          <div className="nest-story-journey-header">
            <div className="nest-story-journey-label">
              <span className="nest-story-journey-pin" aria-hidden>❤️</span>
              <span>FAVOURITE BOOKS</span>
            </div>
            <button
              type="button"
              className="nest-story-journey-path-name"
              onClick={() => onNavigate?.('garden')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Story Path · {favouritePathLabel}
            </button>
          </div>

          <div className="nest-story-journey-track">
            {favouriteBooks.slice(0, 8).map((pathBook, idx) => {
              const nodeStatus = getJourneyNodeStatus(pathBook, book?.id);

              return (
                <React.Fragment key={pathBook.id}>
                  {idx > 0 && (
                    <div className="nest-story-journey-connector is-active" aria-hidden />
                  )}
                  <button
                    type="button"
                    className="nest-story-journey-node"
                    data-testid={`nest-favourite-book-${pathBook.id}`}
                    onClick={() => onOpenReader(pathBook.id)}
                  >
                    <div
                      className={`nest-story-journey-circle nest-story-journey-circle--${nodeStatus}`}
                    >
                      {pathBook.cover_image ? (
                        <img
                          src={`${BACKEND_URL}${pathBook.cover_image}`}
                          alt=""
                          className="nest-story-journey-cover"
                          onError={(ev) => {
                            ev.target.style.display = 'none';
                            const wrap = ev.target.parentElement;
                            if (wrap && !wrap.querySelector('.nest-story-journey-fallback')) {
                              const span = document.createElement('span');
                              span.className = 'nest-story-journey-fallback';
                              span.textContent = pathBook.emoji || '📚';
                              wrap.appendChild(span);
                            }
                          }}
                        />
                      ) : (
                        <span className="nest-story-journey-fallback">
                          {pathBook.emoji || '📚'}
                        </span>
                      )}
                    </div>
                    <span className="nest-story-journey-title">
                      {shortenTitle(pathBook.title)}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default NestPage;
